import { MapKey } from "@/features/auth/types";
import { LeaseComp } from "@/features/leaseComps/types";
import { POI } from "@/features/poi/types";
import { Property } from "@/features/survey/types/survey.types";
import html2canvas from "html2canvas";

// Material Icons codepoint map for reliable rendering (works without ligatures)
const MATERIAL_ICONS_MAP: Record<string, string> = {
  // Common POI icons
  anchor: "\ue308",
  check: "\ue5ca",
  home: "\ue88a",
  star: "\ue838",
  place: "\ue55f",
  location_on: "\ue0c3",
  pin_drop: "\ue55e",
  restaurant: "\ue56c",
  hotel: "\ue53a",
  shopping_cart: "\ue558",
  local_offer: "\ue55f",
  info: "\ue88e",
  room: "\ue805",
  store: "\ue54e",
  business: "\ue0af",
  apartment: "\ue7f4",
  other_houses: "\ue58d",
  domain: "\ue7ee",
  business_center: "\ue7f1",
  location_city: "\ue7f1",
  // Abbreviation fallbacks
  cnd: "\ue0af", // Treat as building/business
  lchr: "\ue53a", // Treat as hotel/location
  poi: "\ue55f", // Place marker
};

/**
 * Converts a Material Icons name to its Unicode codepoint character
 * Falls back to the name if not found (for ligature-based rendering)
 */
export const getMaterialIconChar = (iconName: string | undefined): string => {
  if (!iconName || iconName === "circle") return "";
  const codepoint = MATERIAL_ICONS_MAP[iconName.toLowerCase()];
  return codepoint || iconName; // Fallback to name for ligature rendering
};

// Map configuration constants
export const defaultZoomLevel = 4;
export const defaultMapTextSize = 28;
export const defaultMapCenter = [-95, 40];
export const iconColorYellow = "#F9DD4B";
export const iconColorBlack = "#012A2D";
export const iconColorActive = "#103F60";
export const iconColorInactive = "#0976C3";
export const iconColorShortlist = "#003f2d";
export const iconColorLeaseComp = "#E47444";
export const iconColorPOI = "#6366F1";

export const isEmptyObjectFromStr = (inputStr: string) =>
  Object.keys(JSON.parse(inputStr)).length === 0;

/**
 * Produces a stable composite key identifying a unique map key category.
 * Used to match properties/leaseComps to their legend entry and persist visibility.
 */
export const makeMapKeyCompositeKey = (
  color: string,
  isCurrentLocation: boolean,
  category: string,
): string =>
  `${color.toLowerCase()}|${isCurrentLocation}|${category ?? "Property"}`;
/**
 * Gets icon data configuration for a property marker on the map
 *
 * @param property - The property to get icon data for
 * @returns The configured icon data for the property
 */
export const getIconData = (property: Property) => {
  let bamMapData = {
    iconSize: defaultMapTextSize,
    iconColor: iconColorInactive,
    radius: [] as Array<{ value: string }>,
    radiusLabel: false,
    radiusColor: "",
    radiusLength: "MI",
  };
  if (property && property.isCurrentLocation) {
    bamMapData.iconColor = iconColorYellow;
  }
  if (
    property.bamMapData &&
    typeof JSON.parse(property.bamMapData) === "object" &&
    !isEmptyObjectFromStr(property.bamMapData)
  ) {
    bamMapData = JSON.parse(property.bamMapData);
    if (!("iconSize" in JSON.parse(property.bamMapData))) {
      bamMapData.iconSize = defaultMapTextSize;
    }
    if (!("iconColor" in JSON.parse(property.bamMapData))) {
      if (property.isCurrentLocation) {
        bamMapData.iconColor = iconColorYellow;
      } else {
        bamMapData.iconColor = iconColorInactive;
      }
    }
  }
  if (property.isClientShortlist) {
    bamMapData.iconColor = iconColorShortlist;
  }
  return bamMapData;
};

/**
 * Gets icon data configuration for a property marker, ignoring shortlist status.
 * Use this when you need the base icon color without shortlist override.
 *
 * @param property - The property to get icon data for
 * @returns The configured icon data for the property, excluding isClientShortlist logic
 */
export const getIconDataWithoutShortlist = (property: Property) => {
  let bamMapData = {
    iconSize: defaultMapTextSize,
    iconColor: iconColorInactive,
    radius: [] as Array<{ value: string }>,
    radiusLabel: false,
    radiusColor: "",
    radiusLength: "MI",
  };
  if (property && property.isCurrentLocation) {
    bamMapData.iconColor = iconColorYellow;
  }
  if (
    property.bamMapData &&
    typeof JSON.parse(property.bamMapData) === "object" &&
    !isEmptyObjectFromStr(property.bamMapData)
  ) {
    bamMapData = JSON.parse(property.bamMapData);
    if (!("iconSize" in JSON.parse(property.bamMapData))) {
      bamMapData.iconSize = defaultMapTextSize;
    }
    if (!("iconColor" in JSON.parse(property.bamMapData))) {
      if (property.isCurrentLocation) {
        bamMapData.iconColor = iconColorYellow;
      } else {
        bamMapData.iconColor = iconColorInactive;
      }
    }
  }
  return bamMapData;
};

/**
 * Returns a map of iconColor -> count of properties using that color for their map pin.
 * Uses getIconDataWithoutShortlist so shortlist status does not affect the color grouping.
 *
 * @param properties - Array of properties to count
 * @returns Record<string, number> where key is the iconColor hex and value is the count
 */
export const getPropertyCountByPinColor = (
  properties: Property[],
): Record<string, number> => {
  return properties
    .filter((p) => !p.isHidden)
    .reduce<Record<string, number>>((acc, property) => {
      const { iconColor } = getIconData(property);
      const key = `${iconColor.toLowerCase()}|${property.isCurrentLocation ?? false}|Property`;
      acc[key] = (acc[key] ?? 0) + 1;
      return acc;
    }, {});
};

/**
 * Gets icon data configuration for a lease comp marker on the map.
 * Reads custom styling from leaseComp.mapData if available, otherwise uses defaults.
 *
 * @param leaseComp - The lease comp to get icon data for
 * @returns The configured icon data for the lease comp
 */
export const getLeaseCompIconData = (leaseComp: LeaseComp) => {
  const defaults = {
    iconSize: defaultMapTextSize,
    iconColor: iconColorLeaseComp,
    radius: [] as Array<{ value: string | number }>,
    radiusLabel: false,
    radiusColor: "",
    radiusLength: "MI",
  };

  const mapData = leaseComp.mapData;
  if (!mapData) return defaults;

  return {
    iconSize: mapData.iconSize ?? defaults.iconSize,
    iconColor: mapData.iconColor ?? defaults.iconColor,
    radius: mapData.radius ?? defaults.radius,
    radiusLabel: mapData.radiusLabel ?? defaults.radiusLabel,
    radiusColor: mapData.radiusColor ?? defaults.radiusColor,
    radiusLength: mapData.radiusLength ?? defaults.radiusLength,
  };
};

/**
 * Interface for media metadata object following TypeScript conventions
 */
interface MediaMetadata {
  readonly surveyId: string;
  readonly mediaType: string;
}

/**
 * Interface for market map generation result following clean architecture
 */
interface MarketMapGenerationResult {
  readonly blob: Blob;
  readonly payloadMedia: MediaMetadata;
}

// Extend HTMLCanvasElement to preserve drawing buffer for html2canvas
// eslint-disable-next-line @typescript-eslint/no-explicit-any
(HTMLCanvasElement.prototype.getContext as any) = ((origFn: any) =>
  function (
    this: HTMLCanvasElement,
    type: string,
    attribs?: Record<string, unknown>,
  ) {
    const temp = { ...attribs, preserveDrawingBuffer: true };
    return origFn.call(this, type, temp);
  })(HTMLCanvasElement.prototype.getContext);

export const formatDateMapAPI = (date: Date): string => {
  const isoString = date.toISOString();
  const formattedDate = isoString.replace("T", " ").replace("Z", "");
  return formattedDate;
};

/**
 * Generate market map image from canvas element
 * Following async/await pattern and proper TypeScript error handling
 */
export const marketMapGeneration = async (
  surveyId: string,
): Promise<MarketMapGenerationResult> => {
  try {
    const mapElement = document.getElementById("marketmap-0");
    if (!mapElement) {
      throw new Error('Market map element with ID "marketmap-0" not found');
    }
    // Ensure Material Icons inside inline SVGs render correctly in html2canvas
    await convertMaterialIconsToImages(mapElement);

    // Generate canvas from map element with proper configuration
    const canvas = await html2canvas(mapElement, {
      ignoreElements: (element) =>
        element.classList.contains("mapboxgl-control-container"),
      useCORS: true,
      backgroundColor: null,
      logging: false, // Disable logging in production
      imageTimeout: 0,
    });

    // Convert canvas to blob using Promise-based approach with proper typing
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error("Failed to convert canvas to blob"));
        }
      }, "image/png"); // Add quality parameter for PNG
    });

    // Prepare media metadata following interface structure
    const payloadMedia: MediaMetadata = {
      surveyId,
      mediaType: "MARKETMAP",
    };

    return {
      blob,
      payloadMedia,
    };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";
    console.error("Market map generation failed:", errorMessage);
    throw new Error(`Market map generation failed: ${errorMessage}`);
  }
};

export const getBottomBarHeight = () => {
  const el = document.getElementById("map-key-section"); // your bar element
  if (!el) return 0;
  const rect = el.getBoundingClientRect();
  return rect.height; // pixels
};

export const getDuplicateMapKeys = (mapKeys: MapKey[]): MapKey[] => {
  const makeKey = (item: MapKey) =>
    `${item.color.toLowerCase()}|${item.isCurrentLocation}|${item.category ?? "Property"}`;
  const grouped = mapKeys.reduce<Record<string, MapKey[]>>((acc, key) => {
    const k = makeKey(key);
    acc[k] = acc[k] ? [...acc[k], key] : [key];
    return acc;
  }, {});
  // Keep earliest (lowest order), return the rest as duplicates to remove
  return Object.values(grouped)
    .filter((group) => group.length > 1)
    .flatMap((group) => [...group].sort((a, b) => a.order - b.order).slice(1));
};

/**
 * Filters lease comps to only those that are not hidden.
 * Properties with null or undefined coordinates are still shown.
 */
export const filterLeaseComps = (leaseComps: LeaseComp[]): LeaseComp[] =>
  leaseComps.filter((lc) => !lc.isHidden);

/**
 * Returns all properties without filtering by coordinates.
 * Properties with null or undefined coordinates are still shown.
 */
export const filterPropertiesForMap = (properties: Property[]): Property[] =>
  properties.filter((p) => !p.isHidden);

/**
 * Parses the lat/lng from a LeaseComp's mapData, falling back to leaseCompFields.
 * Returns [lng, lat] tuple when both values are valid numbers, or null otherwise.
 */
export const getLeaseCompCoordinates = (
  leaseComp: LeaseComp,
): [number, number] | null => {
  const lat = parseFloat(leaseComp.leaseCompFields?.latitude || "");
  const lng = parseFloat(leaseComp.leaseCompFields?.longitude || "");
  if (isNaN(lat) || isNaN(lng)) return null;
  return [lng, lat];
};

/**
 * Creates the square SVG HTMLDivElement used for a lease comp marker.
 * Callers can attach additional attributes, popups, or events as needed.
 */
export const createLeaseCompMarkerElement = (
  index: number,
  color: string = iconColorLeaseComp,
  iconSize: number = 26,
): HTMLDivElement => {
  const markerElement = document.createElement("div");
  markerElement.className = "lease-comp-marker";

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", iconSize.toString());
  svg.setAttribute("height", iconSize.toString());
  svg.setAttribute("viewBox", `0 0 ${iconSize} ${iconSize}`);

  // Calculate proportional dimensions based on iconSize (original was 26x26 with 23x23 rect)
  const rectSize = (iconSize * 23) / 26;
  const offset = (iconSize * 1.5) / 26;
  const strokeWidth = (iconSize * 2) / 26;
  const fontSize = (iconSize * 12) / 26;

  const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
  rect.setAttribute("x", offset.toString());
  rect.setAttribute("y", offset.toString());
  rect.setAttribute("width", rectSize.toString());
  rect.setAttribute("height", rectSize.toString());
  rect.setAttribute("rx", "0");
  rect.setAttribute("ry", "0");
  rect.setAttribute("fill", color);
  rect.setAttribute("stroke", "#fff");
  rect.setAttribute("stroke-width", strokeWidth.toString());

  const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
  text.setAttribute("x", "50%");
  text.setAttribute("y", "50%");
  text.setAttribute("text-anchor", "middle");
  text.setAttribute("dy", ".4em");
  text.setAttribute("fill", "#fff");
  text.setAttribute("font-size", fontSize.toString());
  text.setAttribute("font-weight", "bold");
  text.textContent = String(index + 1);

  svg.appendChild(rect);
  svg.appendChild(text);
  markerElement.appendChild(svg);

  return markerElement;
};

/**
 * Creates the inner SVG div element for a property marker (star or numbered circle).
 * Callers wrap this in their own container and add coordinate/popup/event logic.
 */
export const createPropertyMarkerSvgElement = (
  displayContent: string,
  iconColor: string,
  iconSize: number,
): HTMLDivElement => {
  const innerDiv = document.createElement("div");

  if (displayContent === "starIcon") {
    const svgElement = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    svgElement.setAttribute("width", iconSize.toString());
    svgElement.setAttribute("height", iconSize.toString());
    svgElement.setAttribute("viewBox", "0 0 64 64");

    const circleElement = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "circle",
    );
    circleElement.setAttribute("cx", "32");
    circleElement.setAttribute("cy", "32");
    circleElement.setAttribute("r", "28");
    circleElement.setAttribute("fill", iconColor);
    circleElement.setAttribute("stroke", "#fff");
    circleElement.setAttribute("stroke-width", "5");

    const polygonElement = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "polygon",
    );
    polygonElement.setAttribute("fill", "#ffffff");
    polygonElement.setAttribute(
      "points",
      "45.57 51.2 32 43.04 18.46 51.2 22.05 35.81 10.11 25.6 25.86 24.26 32 9.6 38.14 24.1 53.89 25.44 41.95 35.78 45.57 51.2",
    );

    svgElement.appendChild(circleElement);
    svgElement.appendChild(polygonElement);
    innerDiv.appendChild(svgElement);
  } else {
    const svgElement = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    svgElement.setAttribute("width", iconSize.toString());
    svgElement.setAttribute("height", iconSize.toString());
    svgElement.setAttribute("viewBox", "0 0 64 64");

    const circleElement = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "circle",
    );
    circleElement.setAttribute("cx", "32");
    circleElement.setAttribute("cy", "32");
    circleElement.setAttribute("r", "29");
    circleElement.setAttribute("fill", iconColor);
    circleElement.setAttribute("stroke", "#fff");
    circleElement.setAttribute("stroke-width", "5");

    const textElement = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "text",
    );
    textElement.setAttribute("x", "50%");
    textElement.setAttribute("y", "50%");
    textElement.setAttribute("text-anchor", "middle");
    textElement.setAttribute("dy", ".4em");
    textElement.setAttribute("fill", "#fff");
    textElement.setAttribute("font-size", "32");
    textElement.textContent = displayContent || "";

    svgElement.appendChild(circleElement);
    svgElement.appendChild(textElement);
    innerDiv.appendChild(svgElement);
  }

  return innerDiv;
};

/**
 * Parses the lat/lng from a LeaseComp using mapData with fallback to leaseCompFields.
 * Use this for the rendered PDF map view.
 */
export const getLeaseCompCoordinatesWithMapData = (
  leaseComp: LeaseComp,
): [number, number] | null => {
  const lat = parseFloat(
    leaseComp.mapData?.latitude || leaseComp.leaseCompFields?.latitude || "",
  );
  const lng = parseFloat(
    leaseComp.mapData?.longitude || leaseComp.leaseCompFields?.longitude || "",
  );
  if (isNaN(lat) || isNaN(lng)) return null;
  return [lng, lat];
};

/**
 * Gets icon data configuration for a POI marker on the map.
 * Uses the resolved POICategory object (joined from categoryId) for styling:
 *   - ICON pin: circle with iconData.color, optional Material Icon from iconData.iconName
 *   - LOGO pin: image from active logoData.logoLinks URL
 * Falls back to default indigo if no category or color found.
 */
export const getPoiIconData = (poi: POI) => {
  const category = poi.category;
  const iconColor = category?.iconData?.color || "#6366F1";
  const iconName = category?.iconData?.iconName;
  const pointSize = category?.pointSize || defaultMapTextSize;
  const pinType = category?.pinType || "ICON";
  const logoUrl =
    pinType === "LOGO"
      ? (category?.logoData?.logoLinks?.find((l) => l.isActive !== false)
          ?.url ?? category?.logoData?.logoLinks?.[0]?.url)
      : undefined;
  const logoWhiteBackground = category?.logoData?.logoWhiteBackground ?? false;

  return {
    iconSize: pointSize,
    iconColor,
    iconName,
    pinType,
    logoUrl,
    logoWhiteBackground,
  };
};

/**
 * Filters POIs to only those that are not hidden.
 */
export const filterPois = (pois: POI[]): POI[] =>
  pois.filter((p) => !p.isHidden);

/**
 * Parses the lat/lng from a POI, preferring mapData over top-level fields.
 * Returns [lng, lat] tuple when both values are valid numbers, or null otherwise.
 */
export const getPoiCoordinates = (poi: POI): [number, number] | null => {
  const lat = parseFloat(String(poi.mapData?.latitude ?? poi.latitude ?? ""));
  const lng = parseFloat(String(poi.mapData?.longitude ?? poi.longitude ?? ""));
  if (isNaN(lat) || isNaN(lng)) return null;
  return [lng, lat];
};

/**
 * Creates an HTMLDivElement for a POI marker, matching CBREOne-Survey-FE-V2 styling:
 *   - LOGO pinType: rounded <img> using the active logo URL
 *   - ICON pinType: circle SVG with category color + optional Material Icon glyph
 */
export const createPoiMarkerElement = (
  color: string = iconColorPOI,
  iconSize: number = 28,
  iconName?: string,
  pinType: "ICON" | "LOGO" = "ICON",
  logoUrl?: string,
  logoWhiteBackground: boolean = false,
): HTMLDivElement => {
  const markerElement = document.createElement("div");
  markerElement.className = "poi-marker";

  if (pinType === "LOGO" && logoUrl) {
    const img = document.createElement("img");
    img.setAttribute("src", logoUrl);
    img.style.width = `${iconSize}px`;
    img.style.objectFit = "contain";
    img.style.backgroundColor = logoWhiteBackground ? "#ffffff" : "transparent";
    img.style.padding = logoWhiteBackground ? "2px" : "0";
    markerElement.appendChild(img);
    return markerElement;
  }

  // ICON type: circle SVG with Material Icons glyph
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("width", iconSize.toString());
  svg.setAttribute("height", iconSize.toString());
  svg.setAttribute("viewBox", "-1 -1 26 26");
  svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  svg.setAttribute("preserveAspectRatio", "xMidYMid meet");
  svg.style.display = "block";
  svg.style.flexShrink = "0";
  svg.style.borderRadius = "50%";

  const circle = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "circle",
  );
  circle.setAttribute("cx", "12");
  circle.setAttribute("cy", "12");
  circle.setAttribute("r", "12");
  circle.setAttribute("stroke", "#fff");
  circle.setAttribute("stroke-width", "2");
  circle.setAttribute("fill", color);
  svg.appendChild(circle);

  if (iconName && iconName !== "circle") {
    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttribute("x", "12");
    text.setAttribute("y", "12");
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("dominant-baseline", "central");
    text.setAttribute("font-family", "Material Icons");
    text.setAttribute("font-size", "16");
    text.setAttribute("font-weight", "400");
    text.setAttribute("font-style", "normal");
    text.setAttribute("fill", "white");
    text.style.fontFamily = "Material Icons";
    text.style.fontWeight = "400";
    text.style.userSelect = "none";
    text.style.pointerEvents = "none";
    text.textContent = iconName;
    svg.appendChild(text);
  }

  markerElement.appendChild(svg);
  return markerElement;
};

const convertMaterialIconsToImages = async (mapElement: HTMLElement) => {
  await document.fonts.ready;

  const allTexts = mapElement.querySelectorAll("text");
  const materialIconTexts = Array.from(allTexts).filter((text) => {
    const fontFamily =
      text.getAttribute("font-family") ||
      (text as SVGTextElement).style.fontFamily;
    return fontFamily && fontFamily.includes("Material Icons");
  });

  for (const textEl of materialIconTexts) {
    const svgText = textEl as SVGTextElement;
    const iconText = svgText.textContent || "";
    if (!iconText.trim()) continue;

    const fontSize = parseInt(svgText.getAttribute("font-size") || "16", 10);
    const fill = svgText.getAttribute("fill") || "white";

    const tempCanvas = document.createElement("canvas");
    const scale = 4;
    const size = fontSize * scale;
    tempCanvas.width = size;
    tempCanvas.height = size;
    const ctx = tempCanvas.getContext("2d");

    if (!ctx) continue;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.font = `${fontSize * scale}px Material Icons`;
    ctx.fillStyle = fill;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(iconText, size / 2, size / 2);

    const dataUrl = tempCanvas.toDataURL("image/png");
    const svgNS = "http://www.w3.org/2000/svg";
    const image = document.createElementNS(svgNS, "image");

    const textX = parseFloat(svgText.getAttribute("x") || "12");
    const textY = parseFloat(svgText.getAttribute("y") || "12");
    const textAnchor = svgText.getAttribute("text-anchor") || "middle";
    const dominantBaseline =
      svgText.getAttribute("dominant-baseline") || "middle";

    let imgX = textX;
    let imgY = textY;

    if (textAnchor === "middle") {
      imgX = textX - fontSize / 2;
    } else if (textAnchor === "end") {
      imgX = textX - fontSize;
    }

    if (dominantBaseline === "middle" || dominantBaseline === "central") {
      imgY = textY - fontSize / 2;
    } else if (
      dominantBaseline === "alphabetic" ||
      dominantBaseline === "baseline"
    ) {
      imgY = textY - fontSize * 0.75;
    }

    image.setAttribute("x", imgX.toString());
    image.setAttribute("y", imgY.toString());
    image.setAttribute("width", fontSize.toString());
    image.setAttribute("height", fontSize.toString());
    image.setAttributeNS("http://www.w3.org/1999/xlink", "href", dataUrl);

    svgText.parentNode?.replaceChild(image, svgText);
  }
};
